import { DOCUMENT } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, HostBinding, Inject, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { EnvService } from '@core-mkt/services/env/env.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { MarketingSyncUserService } from '@core-mkt/services/sync-user/marketing-sync-user.service';
import { cloneDeep } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { SkipNavComponent } from '@core-mkt/shared/components/skip-nav/skip-nav/skip-nav.component';
import { CollapseLinkComponent } from '../collapse-link/collapse-link.component';
import { HeaderNavLinkComponent } from '../header-nav-link/header-nav-link.component';
import type { HeaderNavLink } from './header';
import { headerData, loggedInMyAccountData, myAccountData } from './header-data';

interface AdaEmbed {
  toggle: () => void;
  setSensitiveMetaFields: (fields: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    adaEmbed?: AdaEmbed;
  }
}

@Component({
  selector: 'ace-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [HeaderNavLinkComponent, CollapseLinkComponent, SkipNavComponent, LazyImgDirective, QueryParamDirective],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @HostBinding('class.sticky-header') headerSticky = false;
  private _activateMobileMenu = false;
  private destroy$ = new Subject<void>();
  headerData: HeaderNavLink[] = headerData;
  myAccountData: HeaderNavLink[] = myAccountData;
  resolverData: PageDataResponse = {} as PageDataResponse;
  showDefaultNav = true;
  showAdaChatbot = false;
  isLoggedIn = false;
  userInitial = '';
  showInitialDropdown = false;

  constructor(
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private env: EnvService,
    private syncUserService: MarketingSyncUserService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    if (this.route.snapshot) {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData);
    }
  }

  ngOnInit(): void {
    if (this.resolverData?.craftData?.headerData) {
      this.headerSticky = this.resolverData.craftData.headerData.isNavSticky;
    }

    this.showDefaultNav = !this.resolverData?.craftData?.headerData?.hideNav;

    const hasAdaChatbotInCraft = !!(
      this.resolverData?.craftData?.pageOptions?.adaChatbot &&
      this.resolverData?.craftData?.pageOptions?.adaChatbot !== ''
    );
    const hasDefaultAda = !!(this.env.get.brandConfig?.defaultAda && this.env.get.brandConfig?.defaultAda !== '');

    this.showAdaChatbot = !hasAdaChatbotInCraft && hasDefaultAda;

    if (this.showAdaChatbot) {
      this.injectHideAdaStyles();
    }

    this.syncUserService.syncUserReady$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      if (response?.success && response.data?.isAuthenticated) {
        this.isLoggedIn = true;
        this.userInitial = response.data.initials?.charAt(0) || '';
        this.myAccountData = loggedInMyAccountData;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private injectHideAdaStyles(): void {
    const styleId = 'hide-ada-button';
    if (!this.document.getElementById(styleId)) {
      const style = this.document.createElement('style');
      style.id = styleId;
      style.textContent = `
        iframe#ada-button-frame {
          display: none !important;
        }
      `;
      this.document.head.appendChild(style);
    }
  }

  openAdaChat(): void {
    // Use Ada's global API to toggle the chat
    if (window.adaEmbed) {
      window.adaEmbed.toggle();
    } else {
      // Fallback: try clicking the button
      const adaButton = this.document.getElementById('ada-chat-button') as HTMLButtonElement;
      if (adaButton) {
        adaButton.click();
      }
    }
  }

  get headerLogo(): string {
    return 'https://xgrit-ecom.imgix.net/ace/Aceable-Logo1.svg?v=1712674234&auto=compress,format';
  }

  get activateMobileMenu(): boolean {
    return this._activateMobileMenu;
  }

  set activateMobileMenu(value: boolean) {
    this._activateMobileMenu = value;
    this.setBodyScroll(!value);
  }

  private setBodyScroll(enable: boolean): void {
    if (enable) {
      this.renderer.removeClass(document.body, 'overflow-hidden');
    } else {
      this.renderer.addClass(document.body, 'overflow-hidden');
    }
  }

  toggleMobileMenu(): void {
    this.activateMobileMenu = !this.activateMobileMenu;
  }
}
